/**
 * Studio Services intake: intro + three steps + thanks.
 *
 * Built for ad traffic, which shapes most choices here:
 *   * The intro and step 1 are one screen — tapping a card IS starting.
 *   * `?type=rsvp` (etc.) preselects a card, so each ad lands on its own offer.
 *   * Each step is a history entry, so the phone's back button goes back a
 *     step instead of leaving the page (and the ad) behind.
 *   * The draft lives in sessionStorage: the Facebook in-app browser reloads
 *     pages it has backgrounded, and a visitor who switched to Messenger for a
 *     minute should not come back to an empty form.
 *   * Contact details come last, after the visitor has already put work in.
 */
import { track } from '@codasc/analytics';
import {
  CATEGORIES,
  type Category,
  LOGO_PAGE,
  categoryFromParam,
  leadValuePesos,
} from '@codasc/studio-shared';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  EMPTY,
  type Errors,
  type FormState,
  checkContact,
  checkDetails,
  stepOfField,
  toBody,
} from './form';
import {
  BelowFold,
  CookieNotice,
  Footer,
  CONTACT_LINKS,
  type ContactLinkId,
  SITE_URL,
  StepAside,
  StepCategory,
  StepContact,
  StepDetails,
  Thanks,
  type ThanksInfo,
} from './steps';
import { LogoPage } from './logo';
import { captureAttribution, hasSession, hasUtm, metaIds, newId, pixelEvent } from './track';
import { Mark, revealFirstError } from './ui';

type Step = 1 | 2 | 3;

const BASE = import.meta.env.BASE_URL; // '/studio/'
const THANKS_PATH = `${BASE}thanks`;
const DRAFT_KEY = 'studio:draft';
const THANKS_KEY = 'studio:thanks';

/**
 * `/studio/logo` is a page, not a step: reached by a full navigation (a link,
 * an ad, a search result) and left by one (`?type=logo` back on the form). So
 * it is decided once, here, and the form's history handling stays out of it.
 */
const ON_LOGO_PAGE = location.pathname.replace(/\/+$/, '') === `${BASE}${LOGO_PAGE.path}`;
const FORM_TITLE =
  'Web apps, AI agents, websites & logos, built in the Philippines — Codasc Studio';

/** Errors reported under `details.*` that belong to a nested state key. */
const NESTED_ERRORS: Record<string, string[]> = {
  rsvp: ['details.eventType'],
  personal: ['details.siteType'],
  agent: ['details.channels', 'details.channelOther'],
  webOpt: ['details.siteUrl', 'details.goals'],
  logo: ['details.need', 'details.currentLogoUrl', 'details.scopeVersion', 'details.layeredSvg'],
};

function load<T>(key: string): T | null {
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function save(key: string, value: unknown): void {
  try {
    if (value === null) sessionStorage.removeItem(key);
    else sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // storage blocked: the form still works, it just won't survive a reload
  }
}

function initialState(): FormState {
  const draft = load<{ state: FormState }>(DRAFT_KEY)?.state;
  const merged: FormState = draft ? { ...EMPTY, ...draft } : EMPTY;
  // A draft can outlive the build that wrote it (a rollback, a card removed).
  // Step 2 indexes its copy by category, so one this build does not know would
  // throw on render and leave a blank page until the tab is closed.
  const known = CATEGORIES.some((c) => c.id === merged.category);
  const base: FormState = known ? merged : { ...merged, category: null, budget: null };
  const fromAd = categoryFromParam(new URLSearchParams(location.search).get('type'));
  return base.category || !fromAd ? base : { ...base, category: fromAd };
}

function initialStep(state: FormState): Step {
  const s = load<{ step: Step }>(DRAFT_KEY)?.step;
  return s && state.category ? s : 1;
}

/**
 * "Message us" in the header: one button, three ways to reach us. A visitor
 * who would rather not use Messenger still has somewhere to go.
 */
function ContactMenu({ onPick }: { onPick: (channel: ContactLinkId) => void }) {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const away = (e: PointerEvent) => {
      if (!wrap.current?.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', away);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('pointerdown', away);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);

  return (
    <div className="st-menu" ref={wrap}>
      <button
        type="button"
        className="st-bar-link st-bar-link-primary st-menu-btn"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="st-long">Message us</span>
        <span className="st-short">Message</span>
        <svg className="st-menu-chev" width="10" height="10" viewBox="0 0 10 10" aria-hidden>
          <path d="M2 3.5 5 6.5 8 3.5" fill="none" stroke="currentColor" strokeWidth="1.6" />
        </svg>
      </button>
      {open ? (
        <div className="st-menu-list">
          {CONTACT_LINKS.map((l) => (
            <a
              key={l.id}
              className="st-menu-item"
              href={l.href}
              {...(l.id === 'email' ? {} : { target: '_blank', rel: 'noopener noreferrer' })}
              onClick={() => {
                onPick(l.id);
                setOpen(false);
              }}
            >
              {l.label}
            </a>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export function App() {
  const attribution = useMemo(() => captureAttribution(), []);
  const [state, setState] = useState<FormState>(initialState);
  const [step, setStep] = useState<Step>(() => initialStep(state));
  const [errors, setErrors] = useState<Errors>({});
  const [sending, setSending] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [thanks, setThanks] = useState<ThanksInfo | null>(() =>
    location.pathname.startsWith(THANKS_PATH) ? load<ThanksInfo>(THANKS_KEY) : null,
  );
  const [signedIn, setSignedIn] = useState(false);
  const started = useRef(false);
  const topRef = useRef<HTMLDivElement>(null);

  const patch = useCallback((p: Partial<FormState>) => {
    setState((s) => ({ ...s, ...p }));
    // Clear the error on whatever was just touched, so the red goes away as
    // soon as the visitor starts fixing it.
    setErrors((e) => {
      let changed = false;
      const next = { ...e };
      for (const key of Object.keys(p)) {
        for (const f of [key, ...(NESTED_ERRORS[key] ?? [])]) {
          if (f in next) {
            delete next[f];
            changed = true;
          }
        }
      }
      return changed ? next : e;
    });
  }, []);

  // Decides which way out the bar offers: the console for a customer, the
  // company site for everyone else. Unknown until /api/me answers, and the
  // stranger's link is the one that shows meanwhile — that is nearly all of
  // this page's traffic, so the swap is the rare case.
  useEffect(() => {
    void hasSession().then(setSignedIn);
  }, []);

  /**
   * Keep the page's own metadata honest as the view changes.
   *
   * The thanks page is one visitor's receipt, so it must not be indexed. The
   * Fastify server serves it with `noindex` already (server/index.ts); this
   * covers the two cases that never reach that route — `pnpm dev`, where the
   * edge sends everything to Vite, and the in-place switch after a send,
   * where no document is fetched at all.
   */
  useEffect(() => {
    const robots = document.querySelector<HTMLMetaElement>('meta[name="robots"]');
    if (robots) robots.content = thanks ? 'noindex,follow' : 'index,follow,max-image-preview:large';
    document.title = thanks
      ? 'Request sent — Codasc Studio'
      : ON_LOGO_PAGE
        ? LOGO_PAGE.title
        : FORM_TITLE;
  }, [thanks]);

  // Persist the draft (never after a successful send — that clears it).
  useEffect(() => {
    if (!thanks && !ON_LOGO_PAGE) save(DRAFT_KEY, { state, step });
  }, [state, step, thanks]);

  // A direct hit on /studio/thanks with nothing to show goes back to the form.
  useEffect(() => {
    if (ON_LOGO_PAGE) return;
    const here = BASE + location.search;
    if (location.pathname.startsWith(THANKS_PATH) && !thanks) {
      history.replaceState({ step: 1 }, '', here);
    } else if (thanks) {
      history.replaceState({ step: 1, thanks: true }, '');
    } else {
      // A restored draft can reopen on step 2 or 3. Rebuild the entries below
      // it so the Back button (history.back) lands on the previous step rather
      // than off the page.
      history.replaceState({ step: 1 }, '', here);
      for (let s = 2; s <= step; s++) history.pushState({ step: s }, '', here);
    }
    // Mount only: this fixes up the entries that already exist.
  }, []);

  // Back / forward move between steps rather than off the page.
  useEffect(() => {
    const onPop = (e: PopStateEvent) => {
      const st = (e.state ?? {}) as { step?: Step; thanks?: boolean };
      if (!location.pathname.startsWith(THANKS_PATH)) setThanks(null);
      setErrors({});
      setStep(st.step ?? 1);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const scrollTop = () => {
    requestAnimationFrame(() => topRef.current?.scrollIntoView({ block: 'start' }));
  };

  const go = (next: Step, category: Category | null = state.category) => {
    setErrors({});
    setFailure(null);
    setStep(next);
    history.pushState({ step: next }, '', BASE + location.search);
    if (next > 1) track('form_step', { step: next, category: category ?? 'unknown' });
    scrollTop();
  };

  const pick = (category: Category) => {
    if (!started.current) {
      started.current = true;
      track('form_start', { category });
    }
    // Switching card resets the budget: a range valid for one category may not
    // be offered for another.
    setState((s) => (s.category === category ? s : { ...s, category, budget: null }));
    go(2, category);
  };

  const nextFromDetails = () => {
    const e = checkDetails(state);
    setErrors(e);
    if (Object.keys(e).length === 0) go(3);
    else revealFirstError(e);
  };

  const submit = async () => {
    const e = checkContact(state);
    setErrors(e);
    if (Object.keys(e).length > 0) {
      revealFirstError(e);
      return;
    }
    if (sending) return;
    setSending(true);
    setFailure(null);
    const eventId = newId();
    try {
      const res = await fetch(`${BASE}api/leads`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(toBody(state, attribution, { eventId, ...metaIds(attribution) })),
      });
      if (res.status === 201) {
        const { ref } = (await res.json()) as { ref: string };
        const category = state.category ?? 'custom';
        // A lead with no value is a lead neither platform can optimise for —
        // they all bid the same. `leadValuePesos` is shared with the server so
        // this event and its CAPI copy cannot report different numbers. Both
        // drop a `value` that arrives without a `currency`, so they travel
        // together or not at all.
        const value = state.budget ? leadValuePesos(category, state.budget) : 0;
        const worth = value > 0 ? { value, currency: 'PHP' } : {};
        pixelEvent('Lead', eventId, { content_category: category, ...worth });
        track('generate_lead', { category, budget: state.budget ?? 'unknown', ...worth });
        const info: ThanksInfo = { ref, name: state.name, channel: state.channel!, category };
        save(THANKS_KEY, info);
        save(DRAFT_KEY, null);
        setThanks(info);
        setState(EMPTY);
        started.current = false;
        history.pushState({ step: 1, thanks: true }, '', THANKS_PATH);
        scrollTop();
        return;
      }
      if (res.status === 400) {
        const body = (await res.json().catch(() => ({}))) as { fields?: string[] };
        const fields = body.fields ?? [];
        const map: Errors = {};
        for (const f of fields) map[f] = 'Please check this.';
        const first = fields[0];
        if (first && stepOfField(first) === 2) go(2);
        setErrors(map);
        setFailure('Something needs another look.');
        // After a step change the fields mount on the next frame.
        requestAnimationFrame(() => revealFirstError(map));
        return;
      }
      setFailure(
        // 429 is either this visitor sending too many or the whole form being
        // flooded (see the api's storage ceiling). One sentence has to be true
        // of both, and both have the same way out.
        res.status === 429
          ? 'We’re getting a lot of requests right now. Message us on Messenger and we’ll pick it up there.'
          : 'Something went wrong on our end. Try again, or message us on Messenger.',
      );
    } catch {
      setFailure('Couldn’t reach us — check your connection and try again.');
    } finally {
      setSending(false);
    }
  };

  const onContact = (channel: ContactLinkId) => {
    const eventId = newId();
    pixelEvent('Contact', eventId);
    track('contact_click', { channel });
    // Server copy, best-effort; keepalive so leaving for Messenger doesn't cancel it.
    void fetch(`${BASE}api/events`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      keepalive: true,
      body: JSON.stringify({
        name: 'Contact',
        eventId,
        sourceUrl: location.href,
        ...metaIds(attribution),
        ...(thanks ? { ref: thanks.ref } : {}),
      }),
    }).catch(() => undefined);
  };

  const onMessenger = () => onContact('messenger');

  const again = () => {
    save(THANKS_KEY, null);
    setThanks(null);
    setErrors({});
    setStep(1);
    history.pushState({ step: 1 }, '', BASE);
    scrollTop();
  };

  // Back from the thanks page lands on a step entry whose form was cleared by
  // the send; with no category there is nothing to show but the start.
  const view: Step = state.category ? step : 1;
  const errorCount = Object.keys(errors).length;

  return (
    <div className="st-page">
      <header className="st-bar">
        <a className="st-brand" href={BASE}>
          <span className="st-brand-tile">
            <Mark size={20} />
          </span>
          <span className="st-brand-wm">
            Codasc <i>Studio</i>
          </span>
        </a>
        <nav className="st-bar-links" aria-label="Elsewhere">
          {/* A customer gets the way back to the console; a stranger gets the
              company, which is what they'd be checking us out on. */}
          {signedIn ? (
            <a className="st-bar-link" href="/apps">
              <span className="st-long">Browse catalog</span>
              <span className="st-short">Catalog</span>
            </a>
          ) : (
            <a className="st-bar-link" href={SITE_URL}>
              <span className="st-long">Visit website</span>
              <span className="st-short">Website</span>
            </a>
          )}
          <ContactMenu onPick={onContact} />
        </nav>
      </header>

      <main className="st-main" ref={topRef}>
        {ON_LOGO_PAGE ? (
          <LogoPage onMessenger={onMessenger} />
        ) : thanks ? (
          <Thanks info={thanks} onMessenger={onMessenger} onAgain={again} />
        ) : view === 1 ? (
          <StepCategory state={state} patch={patch} onPick={pick} errors={errors} />
        ) : (
          <div className="st-step-wrap">
            <div className="st-form">
              <div className="st-progress" aria-label={`Step ${view} of 3`}>
                <button type="button" className="st-back" onClick={() => history.back()}>
                  ← Back
                </button>
                <span className="st-progress-t">Step {view} of 3</span>
                <span className="st-progress-bar" aria-hidden="true">
                  <span style={{ width: `${(view / 3) * 100}%` }} />
                </span>
              </div>

              {view === 2 ? (
                <StepDetails state={state} patch={patch} errors={errors} />
              ) : (
                <StepContact
                  state={state}
                  patch={patch}
                  errors={errors}
                  askHeardFrom={!hasUtm(attribution)}
                />
              )}

              {failure ? (
                <p className="st-failure" role="alert">
                  {failure}
                </p>
              ) : null}

              <div className="st-actions">
                {errorCount > 0 ? (
                  <button
                    type="button"
                    className="st-actions-err"
                    onClick={() => revealFirstError(errors)}
                  >
                    {errorCount === 1 ? '1 field needs a look' : `${errorCount} fields need a look`}{' '}
                    — tap to jump
                  </button>
                ) : null}
                {view === 2 ? (
                  <button
                    type="button"
                    className="st-btn st-btn-primary st-btn-wide"
                    onClick={nextFromDetails}
                  >
                    Continue
                  </button>
                ) : (
                  <button
                    type="button"
                    className="st-btn st-btn-primary st-btn-wide"
                    onClick={() => void submit()}
                    disabled={sending}
                  >
                    {sending ? 'Sending…' : 'Send my request'}
                  </button>
                )}
              </div>
            </div>
            <StepAside step={view} />
          </div>
        )}
      </main>

      {!thanks && view === 1 && !ON_LOGO_PAGE ? <BelowFold /> : null}
      <Footer />
      <CookieNotice show={ON_LOGO_PAGE || (!thanks && view === 1)} />
    </div>
  );
}
